/**
 * Prebuilt report formula fields — user picks a formula + source column(s).
 * Results are computed client-side (Phase 1) via derivedMetrics engine.
 */

import { growthMetricId } from './derivedMetrics';

export function winRateMetricId(numeratorKey, denominatorKey) {
  const slug = (s) =>
    String(s || 'measure')
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .slice(0, 48);
  return `win_rate_${slug(numeratorKey)}_${slug(denominatorKey)}`;
}

export const FORMULA_KEYS = {
  PERIOD_OVER_PERIOD_GROWTH: 'period_over_period_growth',
  WIN_RATE: 'win_rate',
};

export const FORMULA_SOURCE_KINDS = {
  MEASURE: 'measure',
  COUNT: 'count',
  PICKLIST_SLICE: 'picklist_slice',
};

/** Registry of prebuilt formulas (extend here for % of total, running sum, etc.). */
export const FORMULA_DEFINITIONS = {
  [FORMULA_KEYS.PERIOD_OVER_PERIOD_GROWTH]: {
    key: FORMULA_KEYS.PERIOD_OVER_PERIOD_GROWTH,
    label: 'Growth %',
    shortLabel: 'Growth %',
    description: 'Period-over-period change: (Current − Previous) ÷ Previous × 100',
    expression: '((Current − Previous) / Previous) × 100',
    derivedType: 'period_over_period',
    dataType: 'percent',
    requiresRowGroup: true,
    supportsCustomLabel: true,
    pickSource: 'summarized',
    defaultFirstPeriodValue: null,
    defaultDecimals: 1,
    buildOutputFieldName: (sourceFieldName) => growthMetricId(sourceFieldName),
    buildOutputLabel: (sourceLabel, aggregation) => {
      const agg = aggregation ? String(aggregation).toUpperCase() : 'SUM';
      return `Growth % (${agg} of ${sourceLabel || 'measure'})`;
    },
  },
  [FORMULA_KEYS.WIN_RATE]: {
    key: FORMULA_KEYS.WIN_RATE,
    label: 'Win Rate %',
    shortLabel: 'Win Rate %',
    description:
      'Sum of numerator fields ÷ sum of denominator fields × 100 (e.g. Win 8 + 0 over Total 10 → 80%)',
    expression: '(Σ Numerator ÷ Σ Denominator) × 100',
    derivedType: 'win_rate',
    dataType: 'percent',
    requiresRowGroup: false,
    supportsCustomLabel: true,
    pickSource: 'dual',
    defaultDecimals: 1,
    buildOutputFieldName: (numeratorField, denominatorField) =>
      winRateMetricId(numeratorField, denominatorField),
    buildOutputLabel: () => 'Win Rate %',
  },
};

export function listFormulaDefinitions() {
  return Object.values(FORMULA_DEFINITIONS);
}

export function getFormulaDefinition(formulaKey) {
  return FORMULA_DEFINITIONS[formulaKey] || null;
}

/** Encode a formula operand for dropdown value + persisted config. */
export function encodeFormulaSourceKey(source) {
  if (!source?.fieldName) return '';
  if (source.sourceKind === FORMULA_SOURCE_KINDS.PICKLIST_SLICE) {
    const pv = encodeURIComponent(source.picklistValue || '');
    return `pv:${source.picklistGroupField}:${pv}:${source.fieldName}`;
  }
  if (source.sourceKind === FORMULA_SOURCE_KINDS.COUNT) {
    return `count:${source.fieldName}`;
  }
  const agg = source.aggregation ? String(source.aggregation).toLowerCase() : 'sum';
  return `m:${source.fieldName}:${agg}`;
}

export function decodeFormulaSourceKey(value) {
  if (!value || typeof value !== 'string') return null;
  if (value.startsWith('pv:')) {
    const parts = value.split(':');
    if (parts.length < 4) return null;
    const [, picklistGroupField, encodedPv, ...fieldParts] = parts;
    return {
      sourceKind: FORMULA_SOURCE_KINDS.PICKLIST_SLICE,
      fieldName: fieldParts.join(':'),
      picklistGroupField,
      picklistValue: decodeURIComponent(encodedPv),
      aggregation: 'sum',
    };
  }
  if (value.startsWith('count:')) {
    const fieldName = value.slice('count:'.length);
    return {
      sourceKind: FORMULA_SOURCE_KINDS.COUNT,
      fieldName,
      aggregation: 'count',
    };
  }
  if (value.startsWith('m:')) {
    const parts = value.split(':');
    return {
      sourceKind: FORMULA_SOURCE_KINDS.MEASURE,
      fieldName: parts[1],
      aggregation: parts[2] || 'sum',
    };
  }
  return {
    sourceKind: FORMULA_SOURCE_KINDS.MEASURE,
    fieldName: value,
    aggregation: 'sum',
  };
}

function sourceFromConfigSide(side) {
  if (!side) return null;
  return {
    sourceKind: side.source_kind || FORMULA_SOURCE_KINDS.MEASURE,
    fieldName: side.field_name,
    fieldLabel: side.field_label,
    objectName: side.object_name,
    aggregation: side.aggregation || 'sum',
    picklistGroupField: side.picklist_group_field || null,
    picklistValue: side.picklist_value ?? null,
  };
}

export function getFormulaOperandSources(formulaField) {
  const lists = getFormulaOperandLists(formulaField);
  return {
    numerator: lists.numerators[0] || null,
    denominator: lists.denominators[0] || null,
  };
}

/** All numerator / denominator operands (multi-field win rate). */
export function getFormulaOperandLists(formulaField) {
  if (!formulaField) return { numerators: [], denominators: [] };

  if (
    Array.isArray(formulaField.numerator_operands) &&
    formulaField.numerator_operands.length > 0
  ) {
    return {
      numerators: formulaField.numerator_operands
        .map(sourceFromConfigSide)
        .filter((s) => s?.fieldName),
      denominators: (formulaField.denominator_operands || [])
        .map(sourceFromConfigSide)
        .filter((s) => s?.fieldName),
    };
  }

  if (formulaField.numerator?.field_name) {
    return {
      numerators: [sourceFromConfigSide(formulaField.numerator)].filter(
        (s) => s?.fieldName
      ),
      denominators: formulaField.denominator?.field_name
        ? [sourceFromConfigSide(formulaField.denominator)]
        : [],
    };
  }

  return { numerators: [], denominators: [] };
}

export function buildWinRateFieldName(numeratorSources, denominatorSources) {
  const numKeys = (numeratorSources || [])
    .map((s) => encodeFormulaSourceKey(s))
    .filter(Boolean)
    .sort()
    .join('+');
  const denKeys = (denominatorSources || [])
    .map((s) => encodeFormulaSourceKey(s))
    .filter(Boolean)
    .sort()
    .join('+');
  return winRateMetricId(numKeys, denKeys);
}

/**
 * Build a persisted formula field config (stored in report.config.formula_fields).
 */
export function buildFormulaFieldConfig({
  formulaKey,
  sourceFieldName,
  sourceFieldLabel,
  sourceObjectName,
  sourceAggregation = 'sum',
  groupFieldName = null,
  groupFieldLabel = null,
  customLabel = null,
  id = null,
  numeratorSource = null,
  denominatorSource = null,
  numeratorSources = null,
  denominatorSources = null,
}) {
  const def = getFormulaDefinition(formulaKey);
  if (!def) return null;

  if (formulaKey === FORMULA_KEYS.WIN_RATE) {
    const numList =
      numeratorSources?.length > 0
        ? numeratorSources
        : numeratorSource?.fieldName
          ? [numeratorSource]
          : [];
    const denList =
      denominatorSources?.length > 0
        ? denominatorSources
        : denominatorSource?.fieldName
          ? [denominatorSource]
          : [];

    if (!numList.length || !denList.length) return null;

    const fieldName = buildWinRateFieldName(numList, denList);
    const fieldLabel =
      (customLabel && String(customLabel).trim()) ||
      def.buildOutputLabel();

    const toSide = (src) => ({
      field_name: src.fieldName,
      field_label: src.fieldLabel || src.fieldName,
      object_name: src.objectName || null,
      aggregation: src.aggregation || 'sum',
      source_kind: src.sourceKind || FORMULA_SOURCE_KINDS.MEASURE,
      picklist_group_field: src.picklistGroupField || null,
      picklist_value: src.picklistValue ?? null,
    });

    const numeratorOperands = numList.map(toSide);
    const denominatorOperands = denList.map(toSide);

    return {
      id: id || `ff_${fieldName}`,
      field_name: fieldName,
      field_label: fieldLabel,
      formula_key: formulaKey,
      numerator_operands: numeratorOperands,
      denominator_operands: denominatorOperands,
      numerator: numeratorOperands[0],
      denominator: denominatorOperands[0],
      source_field_name: denominatorOperands[0].field_name,
      source_object_name: denominatorOperands[0].object_name || null,
      source_aggregation: denominatorOperands[0].aggregation || 'sum',
      group_field_name: groupFieldName,
      group_field_label: groupFieldLabel,
      data_type: def.dataType,
      is_formula: true,
      decimals: def.defaultDecimals,
    };
  }

  if (!sourceFieldName) return null;

  const fieldName = def.buildOutputFieldName(sourceFieldName);
  const fieldLabel =
    customLabel ||
    def.buildOutputLabel(sourceFieldLabel || sourceFieldName, sourceAggregation);

  return {
    id: id || `ff_${fieldName}`,
    field_name: fieldName,
    field_label: fieldLabel,
    formula_key: formulaKey,
    source_field_name: sourceFieldName,
    source_object_name: sourceObjectName || null,
    source_aggregation: sourceAggregation,
    group_field_name: groupFieldName,
    group_field_label: groupFieldLabel,
    data_type: def.dataType,
    is_formula: true,
    first_period_value: def.defaultFirstPeriodValue,
    decimals: def.defaultDecimals,
  };
}

/**
 * Insert formula columns immediately after their source measure column.
 */
export function buildDisplayFieldsWithFormulas(nonGroupedFields = [], formulaFields = []) {
  const formulas = (formulaFields || [])
    .map((ff) => ({ config: ff, col: formulaFieldToVirtualColumn(ff) }))
    .filter((entry) => entry.col);

  const insertedFormulaNames = new Set();
  const ordered = [];

  const anchorFieldName = (config) => {
    if (config.formula_key === FORMULA_KEYS.WIN_RATE) {
      const { denominator } = getFormulaOperandSources(config);
      return denominator?.fieldName || config.source_field_name;
    }
    return config.source_field_name;
  };

  (nonGroupedFields || []).forEach((field) => {
    if (field?.is_formula) {
      return;
    }
    ordered.push(field);

    formulas.forEach(({ config, col }) => {
      if (insertedFormulaNames.has(config.field_name)) return;
      if (anchorFieldName(config) !== field.field_name) return;
      ordered.push(col);
      insertedFormulaNames.add(config.field_name);
    });
  });

  formulas.forEach(({ config, col }) => {
    if (!insertedFormulaNames.has(config.field_name)) {
      ordered.push(col);
      insertedFormulaNames.add(config.field_name);
    }
  });

  return ordered;
}

/** Virtual column shape for report preview / viewer headers. */
export function formulaFieldToVirtualColumn(formulaField) {
  if (!formulaField) return null;
  const { numerator, denominator } = getFormulaOperandSources(formulaField);
  return {
    id: formulaField.id || `ff_${formulaField.field_name}`,
    field_name: formulaField.field_name,
    field_label: formulaField.field_label,
    object_name: formulaField.source_object_name || '__formula__',
    data_type: formulaField.data_type || 'percent',
    is_formula: true,
    formula_key: formulaField.formula_key,
    source_field_name: formulaField.source_field_name,
    numerator_field_name: numerator?.fieldName,
    denominator_field_name: denominator?.fieldName,
  };
}

/** Convert formula field config → derived-metrics recipe. */
export function formulaFieldToRecipe(formulaField, defaultGroupFieldName = null) {
  if (!formulaField) return null;
  const def = getFormulaDefinition(formulaField.formula_key);
  if (!def) return null;

  const groupField =
    formulaField.group_field_name || defaultGroupFieldName || null;

  if (formulaField.formula_key === FORMULA_KEYS.WIN_RATE) {
    const { numerators, denominators } = getFormulaOperandLists(formulaField);
    if (!numerators.length || !denominators.length) return null;
    const fieldName =
      formulaField.field_name || buildWinRateFieldName(numerators, denominators);
    return {
      id: fieldName,
      label: formulaField.field_label,
      type: def.derivedType,
      groupField,
      decimals: formulaField.decimals ?? def.defaultDecimals,
      formulaKey: formulaField.formula_key,
      numerators,
      denominators,
      numerator: numerators[0],
      denominator: denominators[0],
    };
  }

  const sourceField = formulaField.source_field_name;
  return {
    id: formulaField.field_name,
    label: formulaField.field_label,
    type: def.derivedType,
    sourceField,
    groupField,
    firstPeriodValue:
      formulaField.first_period_value ?? def.defaultFirstPeriodValue,
    decimals: formulaField.decimals ?? def.defaultDecimals,
    formulaKey: formulaField.formula_key,
  };
}

export function collectFormulaFieldsFromConfig(reportConfig) {
  if (reportConfig == null) return [];
  let cfg = reportConfig;
  if (typeof cfg === 'string') {
    try {
      cfg = JSON.parse(cfg);
    } catch {
      return [];
    }
  }
  const list = cfg?.formula_fields;
  return Array.isArray(list) ? list.filter((f) => f?.field_name && f?.formula_key) : [];
}

/** Summarized measures available as formula sources. */
export function listSummarizedSourceOptions(selectedFields, summarizedFields, selectedGroups) {
  const groupNames = new Set(
    (selectedGroups || []).map((g) => g.field_name)
  );
  const options = [];

  (selectedFields || []).forEach((field) => {
    if (groupNames.has(field.field_name)) return;
    const sum = summarizedFields?.[field.id];
    if (!sum?.aggregation) return;
    const source = {
      fieldName: field.field_name,
      fieldLabel: field.field_label || field.field_name,
      objectName: field.object_name,
      aggregation: sum.aggregation,
      fieldId: field.id,
      sourceKind: FORMULA_SOURCE_KINDS.MEASURE,
    };
    options.push({
      value: encodeFormulaSourceKey(source),
      label: `${String(sum.aggregation).toUpperCase()} of ${source.fieldLabel}`,
      ...source,
    });
  });

  return options;
}

function isPicklistField(field) {
  const dt = String(field?.data_type || field?.field_type || '').toLowerCase();
  return dt === 'picklist' || dt === 'multipicklist';
}

function picklistValuesForGroupField(groupField, reportFields = []) {
  const fromField = groupField?.picklist_values || groupField?.picklist_values_data;
  if (Array.isArray(fromField) && fromField.length) return fromField;
  const match = (reportFields || []).find(
    (f) =>
      f.field_name === groupField?.field_name &&
      (f.object_name || 'main') === (groupField?.object_name || 'main')
  );
  return match?.picklist_values || match?.picklist_values_data || [];
}

/**
 * All operands for formulas: summarized measures, row counts, and picklist column slices.
 */
export function listFormulaSourceOptions(
  selectedFields = [],
  summarizedFields = {},
  selectedGroups = [],
  selectedColumnGroups = [],
  reportFields = []
) {
  const options = listSummarizedSourceOptions(
    selectedFields,
    summarizedFields,
    [...selectedGroups, ...selectedColumnGroups]
  );
  const seen = new Set(options.map((o) => o.value));

  const groupNames = new Set(
    [...selectedGroups, ...selectedColumnGroups].map((g) => g.field_name)
  );

  (selectedFields || []).forEach((field) => {
    if (groupNames.has(field.field_name)) return;
    if (summarizedFields?.[field.id]?.aggregation) return;
    const source = {
      fieldName: field.field_name,
      fieldLabel: field.field_label || field.field_name,
      objectName: field.object_name,
      aggregation: 'count',
      fieldId: field.id,
      sourceKind: FORMULA_SOURCE_KINDS.COUNT,
    };
    const value = encodeFormulaSourceKey(source);
    if (seen.has(value)) return;
    seen.add(value);
    options.push({
      value,
      label: `Count of ${source.fieldLabel}`,
      ...source,
    });
  });

  const countCapableOptions = options.filter(
    (o) =>
      o.sourceKind === FORMULA_SOURCE_KINDS.COUNT ||
      o.sourceKind === FORMULA_SOURCE_KINDS.MEASURE
  );

  const addPicklistSliceOptions = (groupField, axisLabel) => {
    if (!isPicklistField(groupField)) return;
    const values = picklistValuesForGroupField(groupField, reportFields);
    if (!values.length) return;

    const groupLabel = groupField.field_label || groupField.field_name;
    const sorted = [...values].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
    );

    const pushSlice = (source, label) => {
      const value = encodeFormulaSourceKey(source);
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ value, label, ...source });
    };

    sorted.forEach((pv) => {
      const pickLabel =
        pv.value_label || pv.label || pv.value || pv.value_key || pv.key;
      if (pickLabel == null || pickLabel === '') return;

      countCapableOptions.forEach((m) => {
        pushSlice(
          {
            fieldName: m.fieldName,
            fieldLabel: m.fieldLabel,
            objectName: m.objectName,
            aggregation:
              m.sourceKind === FORMULA_SOURCE_KINDS.COUNT
                ? 'count'
                : m.aggregation || 'sum',
            sourceKind: FORMULA_SOURCE_KINDS.PICKLIST_SLICE,
            picklistGroupField: groupField.field_name,
            picklistValue: String(pickLabel),
          },
          `${m.label} — ${pickLabel} (${groupLabel}, ${axisLabel})`
        );
      });
    });

    countCapableOptions.forEach((m) => {
      pushSlice(
        {
          fieldName: m.fieldName,
          fieldLabel: m.fieldLabel,
          objectName: m.objectName,
          aggregation:
            m.sourceKind === FORMULA_SOURCE_KINDS.COUNT
              ? 'count'
              : m.aggregation || 'sum',
          sourceKind: FORMULA_SOURCE_KINDS.PICKLIST_SLICE,
          picklistGroupField: groupField.field_name,
          picklistValue: '__all__',
        },
        `${m.label} — all ${groupLabel} (${axisLabel})`
      );
    });
  };

  selectedColumnGroups.forEach((g) =>
    addPicklistSliceOptions(g, 'column group')
  );
  selectedGroups.forEach((g) => addPicklistSliceOptions(g, 'row group'));

  return options;
}

export function formatFormulaDisplayValue(value, dataType = 'percent') {
  if (value == null || value === '') return '--';
  if (dataType === 'percent') {
    const n = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(n)) return '--';
    return `${n}%`;
  }
  return String(value);
}

function mapHasValue(map, key) {
  return (
    map != null &&
    Object.prototype.hasOwnProperty.call(map, key)
  );
}

/**
 * Lookup computed formula value for a group bucket label.
 */
export function getFormulaValueForGroup(summarizedData, formulaField, groupLabel) {
  if (!summarizedData || !formulaField?.field_name || groupLabel == null) return null;
  const map = summarizedData[formulaField.field_name];
  if (!map || typeof map !== 'object') return null;

  const label = String(groupLabel).trim();
  if (mapHasValue(map, label)) return map[label];
  if (mapHasValue(map, groupLabel)) return map[groupLabel];

  const target = label.toLowerCase();
  for (const [k, v] of Object.entries(map)) {
    if (k.startsWith('_')) continue;
    if (String(k).trim().toLowerCase() === target) return v;
  }

  if (mapHasValue(map, '__all__')) return map.__all__;
  const keys = Object.keys(map).filter((k) => !k.startsWith('_'));
  if (keys.length === 1) return map[keys[0]];
  return null;
}
